import React from "react";
import { useNavigate } from "react-router-dom";
import { ArrowRight, BarChart3 } from "lucide-react";
import { useLaunchParams } from "@telegram-apps/sdk-react";
import { DesktopLayout } from "@/layouts";
import { useReferrals } from "@/providers/ReferralsProvider";
import { useUserEvents, useUserStats } from "@/hooks/useLeaderboard";
import {
  HowItWorksCard,
  LeaderboardTable,
  MissionsList,
  ReferralCodeCard,
  ReferralStatCards,
} from "@/components/referrals/ReferralSections";

/**
 * Desktop version of the Referrals page
 */
export const ReferralsPageDesktop: React.FC = () => {
  const navigate = useNavigate();
  const launchParams = useLaunchParams();
  const tgUser = launchParams.initData?.user;
  const userId = tgUser?.id?.toString();
  const { data, isWhitelisted, handleCopyCode, handleShareCode } =
    useReferrals();
  const { data: events } = useUserEvents(userId);
  const { data: userStats } = useUserStats(userId);

  if (!data || !isWhitelisted) {
    return null;
  }

  const userPoints = userStats?.totalPoints ?? 0;
  const userMultiplier = userStats?.multiplier ? userStats.multiplier / 100 : 1;
  const missions = [...(events ?? [])].sort((a, b) => a.orderIndex - b.orderIndex);
  const youName = tgUser?.username || tgUser?.firstName || "You";

  return (
    <DesktopLayout
      pageTitle="Invite. Trade."
      pageTitleAccent="Earn Together."
      pageDescription="Bring your friends to Trigger and earn points as they trade. The more you refer, the more you earn."
      showRightSidebar={false}
    >
      <div className="grid items-start gap-6 xl:grid-cols-[minmax(0,1fr)_400px]">
        <div className="flex flex-col gap-6 xl:order-2">
          <ReferralCodeCard
            referralCode={data.referralStats.referralCode}
            codeNeedsRefresh={data.referralStats.codeNeedsRefresh}
            canUseCode={data.referralStats.canUseCode}
            onCopyCode={handleCopyCode}
            onShareCode={handleShareCode}
            onGenerateNewCode={() => navigate("/generate-new-code")}
          />
          <div className="hidden xl:block">
            <HowItWorksCard tall />
          </div>
        </div>

        <div className="flex min-w-0 flex-col gap-6 xl:order-1">
          <ReferralStatCards
            referrals={data.referralStats.totalReferrals}
            points={userPoints}
            multiplier={userMultiplier}
          />

          <section className="flex flex-col gap-5 border-t border-white/10 pt-6">
            <div>
              <h3 className="text-xl font-semibold leading-[30px] text-white">Level up your Trading</h3>
              <p className="text-base leading-6 text-[#a3a3a3]">
                Complete referral and trading milestones to earn more points and increase your multiplier.
              </p>
            </div>
            <MissionsList events={missions} />
          </section>

          <section className="glass flex flex-col !rounded-2xl">
            <div className="flex items-center justify-between px-4 py-4">
              <div className="flex items-center gap-3">
                <span className="flex size-12 items-center justify-center rounded-full border border-[#2dcc89]/60 bg-[#07a66c]/10 text-[#2dcc89]">
                  <BarChart3 className="size-6" />
                </span>
                <div>
                  <p className="text-base font-semibold leading-6 text-white">Leaderboard</p>
                  <p className="text-sm leading-5 text-[#a3a3a3]">Top referrers this season</p>
                </div>
              </div>
              <button
                type="button"
                onClick={() => navigate("/leaderboard")}
                className="flex items-center gap-1.5 rounded-lg border border-white/10 px-3.5 py-2 text-sm font-semibold text-[#d4d4d4] transition-colors hover:border-white/25"
              >
                View all
                <ArrowRight className="size-5" />
              </button>
            </div>
            <LeaderboardTable
              leaderboard={data.leaderboard}
              you={{ name: youName, rank: data.referralStats.userRank, points: userPoints }}
            />
          </section>

          <div className="xl:hidden">
            <HowItWorksCard />
          </div>
        </div>
      </div>
    </DesktopLayout>
  );
};
